import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MessageCircle, MapPin, Clock, Instagram, Facebook, Send, CheckCircle2, Paperclip } from "lucide-react";
import { CATEGORIES, CONFIG, fadeUp } from "../../data";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = { full_name: "", mobile: "", email: "", location: "", subject: "", category: "", message: "" };

export const ContactSection = () => {
  const [form, setForm] = useState(initial);
  const [file, setFile] = useState(null);
  const [company, setCompany] = useState(""); // honeypot
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const er = {};
    if (form.full_name.trim().length < 2) er.full_name = "Please enter your name";
    if (!/^[0-9+\-\s()]{6,20}$/.test(form.mobile)) er.mobile = "Enter a valid mobile number";
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) er.email = "Enter a valid email";
    if (form.subject.trim().length < 2) er.subject = "Please add a subject";
    if (!form.category) er.category = "Select a category";
    if (form.message.trim().length < 5) er.message = "Please write your message";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) { toast.error("Please check the highlighted fields."); return; }
    setLoading(true);
    try {
      let message = form.message;
      if (file) message += `\n\n[Attachment provided by citizen: ${file.name}]`;
      const res = await axios.post(`${API}/contact`, { ...form, message, company });
      if (res.data.status === "success") {
        setDone(true);
        toast.success("Thank you. Your message has been received.");
        setForm(initial); setFile(null);
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again or use WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full rounded-xl bg-white/80 border px-4 py-3 font-body text-[15px] text-[var(--charcoal)] placeholder:text-[#98a0a8] focus:outline-none focus:ring-2 focus:ring-[var(--navy)]/40 transition-shadow";

  return (
    <section id="contact" className="relative py-24 md:py-32 overflow-hidden" style={{ background: "var(--navy)" }}>
      <div className="absolute inset-0 opacity-60" style={{ background: "radial-gradient(800px circle at 90% 10%, rgba(25,118,210,0.16), transparent 45%)" }} />
      <div className="karnataka-bg absolute inset-y-0 left-0 w-1/2 opacity-[0.08]" style={{ backgroundImage: "url(/photos/karnataka.svg)" }} aria-hidden />
      <div className="container-x relative z-10 grid lg:grid-cols-12 gap-12">
        {/* left info */}
        <div className="lg:col-span-5">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <span className="eyebrow" style={{ color: "var(--gold)" }}>Get in Touch</span>
            <h2 className="font-display font-bold tracking-tight text-3xl md:text-4xl lg:text-5xl mt-4 text-ivory">
              Got something to share? We&apos;re all ears.
            </h2>
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="glass-dark rounded-2xl p-7 mt-10 space-y-5">
            <div className="flex items-start gap-4">
              <MapPin size={20} color="var(--gold)" className="mt-1 shrink-0" />
              <div>
                <p className="font-display text-ivory font-medium">Office</p>
                <p className="font-body text-ivory/75 text-sm leading-relaxed mt-1">
                  Shezan Lavelle, 4th Floor, No. 15, Walton Road,<br />Shanthala Nagar, Ashok Nagar, Bengaluru, Karnataka – 560001
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Clock size={20} color="var(--gold)" className="mt-1 shrink-0" />
              <div>
                <p className="font-display text-ivory font-medium">Office Hours</p>
                <p className="font-body text-ivory/75 text-sm mt-1">Mon – Sat: 9:30 AM – 6:30 PM · Sunday: Closed</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Mail size={20} color="var(--gold)" className="mt-1 shrink-0" />
              <div>
                <p className="font-display text-ivory font-medium">Email</p>
                <a href={`mailto:${CONFIG.email}`} className="font-body text-ivory/85 text-sm mt-1 hover:text-white transition-colors">{CONFIG.email}</a>
              </div>
            </div>
          </motion.div>

          <div className="flex flex-wrap gap-3 mt-6">
            <a data-testid="contact-whatsapp" href={`https://wa.me/${CONFIG.whatsapp}?text=${encodeURIComponent(CONFIG.waMessage)}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-5 py-3 font-body text-sm font-semibold text-white hover:-translate-y-0.5 transition-transform" style={{ background: "#25D366" }}>
              <MessageCircle size={16} /> WhatsApp
            </a>
            <a data-testid="contact-maps" href={CONFIG.maps} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-5 py-3 font-body text-sm font-semibold text-ivory border border-white/20 hover:bg-white/5 transition-colors">
              <MapPin size={16} /> Directions
            </a>
            <a data-testid="contact-instagram" href={CONFIG.instagram} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center h-11 w-11 rounded-full text-ivory border border-white/20 hover:bg-white/5 transition-colors" aria-label="Instagram">
              <Instagram size={18} />
            </a>
            <a data-testid="contact-facebook" href={CONFIG.facebook} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center h-11 w-11 rounded-full text-ivory border border-white/20 hover:bg-white/5 transition-colors" aria-label="Facebook">
              <Facebook size={18} />
            </a>
          </div>
        </div>

        {/* form */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="lg:col-span-7">
          {done ? (
            <div data-testid="contact-success" className="glass-light rounded-3xl p-10 md:p-14 h-full flex flex-col items-center justify-center text-center">
              <CheckCircle2 size={56} color="var(--green)" />
              <h3 className="font-display font-bold text-2xl md:text-3xl mt-6" style={{ color: "var(--navy)" }}>Thank you — your message is with us.</h3>
              <p className="font-body mt-3" style={{ color: "var(--text2)" }}>We'll read it carefully and get back where we can. For anything urgent, a quick WhatsApp is the fastest way to reach us.</p>
              <button data-testid="contact-send-another" onClick={() => setDone(false)} className="btn-primary mt-8 rounded-full font-body font-semibold px-7 py-3">
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="glass-light rounded-3xl p-7 md:p-10 shadow-[0_20px_40px_rgba(0,0,0,0.15)]">
              <input type="text" tabIndex={-1} autoComplete="off" value={company} onChange={(e) => setCompany(e.target.value)}
                className="hidden" aria-hidden="true" />
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="font-body text-sm font-medium" style={{ color: "var(--navy)" }}>Full Name *</label>
                  <input data-testid="input-full-name" className={`mt-1.5 ${field}`} value={form.full_name} onChange={set("full_name")} placeholder="Your name" />
                  {errors.full_name && <p className="text-xs mt-1" style={{ color: "#c0392b" }}>{errors.full_name}</p>}
                </div>
                <div>
                  <label className="font-body text-sm font-medium" style={{ color: "var(--navy)" }}>Mobile Number *</label>
                  <input data-testid="input-mobile" className={`mt-1.5 ${field}`} value={form.mobile} onChange={set("mobile")} placeholder="+91 " />
                  {errors.mobile && <p className="text-xs mt-1" style={{ color: "#c0392b" }}>{errors.mobile}</p>}
                </div>
                <div>
                  <label className="font-body text-sm font-medium" style={{ color: "var(--navy)" }}>Email Address</label>
                  <input data-testid="input-email" className={`mt-1.5 ${field}`} value={form.email} onChange={set("email")} placeholder="you@example.com" />
                  {errors.email && <p className="text-xs mt-1" style={{ color: "#c0392b" }}>{errors.email}</p>}
                </div>
                <div>
                  <label className="font-body text-sm font-medium" style={{ color: "var(--navy)" }}>Location / Area</label>
                  <input data-testid="input-location" className={`mt-1.5 ${field}`} value={form.location} onChange={set("location")} placeholder="City / locality" />
                </div>
                <div>
                  <label className="font-body text-sm font-medium" style={{ color: "var(--navy)" }}>Subject *</label>
                  <input data-testid="input-subject" className={`mt-1.5 ${field}`} value={form.subject} onChange={set("subject")} placeholder="Brief subject" />
                  {errors.subject && <p className="text-xs mt-1" style={{ color: "#c0392b" }}>{errors.subject}</p>}
                </div>
                <div>
                  <label className="font-body text-sm font-medium" style={{ color: "var(--navy)" }}>Category *</label>
                  <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger data-testid="select-category" className={`mt-1.5 ${field} justify-between`}>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`category-${c.toLowerCase().replace(/\s/g, "-")}`}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {errors.category && <p className="text-xs mt-1" style={{ color: "#c0392b" }}>{errors.category}</p>}
                </div>
              </div>

              <div className="mt-4">
                <label className="font-body text-sm font-medium" style={{ color: "var(--navy)" }}>Message *</label>
                <textarea data-testid="input-message" rows={4} className={`mt-1.5 ${field} resize-none`} value={form.message} onChange={set("message")} placeholder="Share your concern, suggestion or request…" />
                {errors.message && <p className="text-xs mt-1" style={{ color: "#c0392b" }}>{errors.message}</p>}
              </div>

              <label className="mt-4 flex items-center gap-2 font-body text-sm cursor-pointer" style={{ color: "var(--text2)" }}>
                <Paperclip size={16} />
                <span>{file ? file.name : "Attach a file (optional)"}</span>
                <input data-testid="input-attachment" type="file" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} />
              </label>

              <button data-testid="contact-submit" type="submit" disabled={loading}
                className="btn-primary mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full font-body font-semibold py-4 disabled:opacity-60">
                {loading ? "Sending…" : <>Send Message <Send size={17} /></>}
              </button>
              <p className="font-body text-xs text-center mt-3" style={{ color: "var(--muted)" }}>Your details are shared only with the office of Puttraj.</p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
